import {AppFunction, HashUrl, ROLE_CASUAL_DELIVERY, ROLE_DELIVERY} from "../misc/types";
import * as utils from "../misc/utils";
import {
    AddOrderFunction,
    AllOrdersForDeliveryFunction,
    EditOrderFunction, InvoiceFunction, ListActivityFunction,
    AddPaymentFunction, DeliveryMapFunction,
    ListOrdersFunction,
    MapOrdersFunction, NewOrdersForDeliveryFunction,
    NewOrdersForPickupFunction,
    NewOrdersFunction,
    NumberFunction,
    OrdersFunction,
    OrdersTodayFunction,
    PrintOrdersFunction,
    ScanFunction,
    ViewOrderFunction,
    ViewOrderImageFunction, CardFunction, AllManualOrdersFunction
} from "./orders";
import {LoginFunction, LogoutFunction} from "./login";
import {ProductsFunction} from "./products";
import {UsersFunction} from "./users";
import {User} from "../bo/user";
import {App} from "../app/app";
import {PaymentFunction} from "./payment";
import {DeliveryFunction, PickupsFunction} from "./delivery";
import {AboutFunction} from "./about";
import {ReportsFunction} from "./reports";
import {WixFunction} from "./wix";
import {ConfigFunction} from "./config";
import {EditMenuFunction, EditPageFunction, EditWebsiteFunction} from "./website";
import {ShopCollectionFunction, ShopCollectionsFunction, ShopProductFunction, ShopProductsFunction} from "./shop";
import {BackupFunction} from "./backup";
import {RrWebFunction} from "./rrweb";
import {SessionsFunction} from "./sessions";
import {InventoryFunction} from "./inventory";
import {CardPrintFunction, FixHandwritingFunction} from "./handwriting";


export class Factory {

    private fnCache : { [ key : string ] : AppFunction } = {};

    private app : App;

    constructor(app: App) {
        this.app = app;
    }

    fromUrl(url: HashUrl, user: User | undefined): AppFunction {

        let fn = this.fnCache[url];
        if (fn) {
            fn.refresh();
            return fn;
        }

        fn = this.fromUrl0(url, user);

        if (fn.isCacheable()) {
            this.fnCache[url] = fn;
        }

        return fn;
    }

    private fromUrl0(url: HashUrl, user: User | undefined): AppFunction {

        const path = utils.path(url);

        console.log("fromUrl0", url, user);

        function match(prefix: string) {
            const p = url.substr(0, prefix.length);
            return (p === prefix);
        }

        if (match("/orders/print")) {
            const ids = path[2].split(",");
            return new PrintOrdersFunction(ids);
        }
        if (match("/orders/card")) {
            const ids = path[2] ? path[2].split(",") : [];
            return new CardFunction(ids);
        }
        if (match("/orders/map/deliveries")) {
            const dateStr = path[3];
            const dates = dateStr.split(/,/);
            return new DeliveryMapFunction(dates);
        }
        if (match("/orders/map")) {
            const ids = path[2] ? path[2].split(",") : undefined;
            return new MapOrdersFunction(ids);
        }
        if (match("/orders/invoice") && path[2]) {
            const id = path[2];
            return new InvoiceFunction(id);
        }
        if (match("/orders/list")) {
            const ids = path[2] ? path[2].split(",") : undefined;
            return new ListOrdersFunction(ids);
        }
        if (match("/orders/view")) {
            const id = path[2];
            return new ViewOrderFunction(this.app, id);
        }
        if (match("/orders/new/pickups")) {
            const dateStr = path[3];
            const dates = dateStr.split(/,/);
            return new NewOrdersForPickupFunction(dates);
        }
        if (match("/orders/new/deliveries")) {
            const dateStr = path[3];
            const dates = dateStr.split(/,/);
            return new NewOrdersForDeliveryFunction(dates);
        }
        if (match("/orders/all/deliveries")) {
            const dateStr = path[3];
            const dates = dateStr.split(/,/);
            return new AllOrdersForDeliveryFunction(dates);
        }
        if (match("/orders/all/manual")) {
            const dateStr = path[3];
            const dates = dateStr.split(/,/);
            return new AllManualOrdersFunction(dates);
        }
        if (match("/orders/new")) {
            return new NewOrdersFunction();
        }
        if (match("/orders/add/payment")) {
            return new AddPaymentFunction(this.app);
        }
        if (match("/orders/add")) {
            return new AddOrderFunction(this.app);
        }
        if (match("/orders/edit")) {
            const id = path[2];
            return new EditOrderFunction(this.app, id);
        }
        if (match("/orders/today")) {
            const status = path[2];
            return new OrdersTodayFunction(status);
        }

        if (match("/orders")) {
            return new OrdersFunction();
        }
        if (match("/qr")) {
            const id = path[1];
            return new ViewOrderFunction(this.app, id);
        }
        if (match("/img")) {
            const id = path[1];
            return new ViewOrderImageFunction(id);
        }
        if (match("/pickups")) {
            const id = path[1];
            return new PickupsFunction();
        }
        if (match("/activity")) {
            return new ListActivityFunction();
        }

        if (match("/scan") && user) {
            return new ScanFunction();
        }

        if (match("/number") && user) {
            return new NumberFunction();
        }

        if (match("/products")) {
            return new ProductsFunction();
        }

        if (match("/shop/collections")) {
            return new ShopCollectionsFunction();
        }

        if (match("/shop/collection")) {
            return new ShopCollectionFunction(path[2]);
        }

        if (match("/shop/products")) {
            return new ShopProductsFunction();
        }

        if (match("/shop/product")) {
            return new ShopProductFunction(path[2]);
        }

        if (match("/backup")) {
            return new BackupFunction();
        }

        if (match("/users")) {
            return new UsersFunction();
        }

        if (match("/payment")) {
            return new PaymentFunction();
        }

        if (match("/deliveries")) {
            return new DeliveryFunction();
        }

        if (match("/set-deliveries")) {
            const ids = path[1].split(/,/);
            return new DeliveryFunction(ids);
        }

        if (match("/config")) {
            return new ConfigFunction();
        }

        if (match("/wix") && user && user.admin) {
            return new WixFunction();
        }

        if (match("/reports") && user) {
            return new ReportsFunction(user);
        }

        if (match("/website/edit/page")) {
            return new EditPageFunction(path[3]);
        }
        if (match("/website/edit/menu")) {
            return new EditMenuFunction(path[3]);
        }

        if (match("/website/edit")) {
            return new EditWebsiteFunction();
        }

        if (match("/handwriting/fix")) {
            return new FixHandwritingFunction();
        }

        if (match("/cardprint")) {
            return new CardPrintFunction();
        }

        if (match("/login")) {
            return new LoginFunction(this.app);
        }

        if (match("/logout")) {
            return new LogoutFunction(this.app);
        }

        if (match("/about")) {
            const admin = user && user.admin ? true : false;
            return new AboutFunction(admin);
        }

        if (match("/sessions")) {
            const admin = user && user.admin ? true : false;
            return new SessionsFunction(admin);
        }

        if (match("/inventory")) {
            const admin = user && user.admin ? true : false;
            return new InventoryFunction(admin);
        }

        if (match("/rrweb")) {
            const session = path[1];
            return new RrWebFunction(session);
        }

        if (! user) {
            return new LoginFunction(this.app);
        }

        console.log("Home function for user", user);

        if (user.role == ROLE_DELIVERY) {
            return new DeliveryFunction();
        }

        if (user.role == ROLE_CASUAL_DELIVERY) {
            return new DeliveryFunction();
        }

        return new NewOrdersFunction();
    }

}
